import { Component, OnInit, Inject, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgForm, ValidationErrors } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { CustomerService } from '../services/customer.service';
import { Customer } from '../../shared/models/customer.interface';
import { Office } from '../../shared/models/office.interface';
import { User } from '../../shared/models/user.interface';
import { LeadSourceType } from '../../shared/models/lead.source.type.interface';
import { CustomerType } from '../../shared/models/customer.type.interface';
import { PaymentType } from '../../shared/models/payment.type.interface';
import { PaymentTerm } from '../../shared/models/payment.term.interface';
import { ContactType } from '../../shared/models/contact.type.interface';
import { ProjectCustomerEdit } from '../../shared/models/project.customer.edit.interface';
import {RolePermission } from '../../shared/models/role.permission.enum';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MatSnackBar, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { AbstractBiditComponent } from '../../shared/controls/abstract.bidit.component';

@Component({
  selector: 'app-customer-info',
  templateUrl: './customer.info.component.html',
  styleUrls: ['./customer.info.component.scss']
})
export class CustomerInfoComponent extends AbstractBiditComponent {
  customer: ProjectCustomerEdit = {} as ProjectCustomerEdit;

  readOnly: boolean = true;

  customers: Customer[];
  customerTypes: CustomerType[];
  contactTypes: ContactType[];
  offices: Office[];
  states: string[];
  leadSourceTypes: LeadSourceType[];
  salespersons: User[];
  paymentTerms: PaymentTerm[];
  paymentTypes: PaymentType[];

  constructor(private customerService: CustomerService, deviceDetectorService: DeviceDetectorService, private snackBar: MatSnackBar, private router: Router) {
    super(deviceDetectorService);
  }

  @Input()
  public set customerObject(value: Customer) {
    if (value) {
      // clone object to keep changes local
      let customer = (<any>Object).assign({}, value) as Customer;
      this.loadCustomer(customer);
    }
  }

  @Output() updated = new EventEmitter<Customer>();

  ngOnInit() {   
    var isNewCustomer = (this.customer.customer_ID === 0 || this.customer.customer_ID === null || this.customer.customer_ID === undefined);
    this.readOnly = !CustomerService.hasPermission(RolePermission.EditCustomer) && (!isNewCustomer && CustomerService.hasPermission(RolePermission.AddCustomer));

    this.customerService.getCustomerTypes()
      .subscribe({
        next: (data) => {
          this.customerTypes = data;
        },
        error: (error: string) => this.onError(error)
      });

    this.customerService.getContactTypes()
      .subscribe({
        next: (data) => {
          this.contactTypes = data;
        },
        error: (error: string) => this.onError(error)
      });

    this.customerService.getOffices()
      .subscribe({
        next: (data) => {
          this.offices = data;
        },
        error: (error: string) => this.onError(error)
      });

    this.customerService.getSalespersons(true)
      .subscribe({
        next: (data) => {
          this.salespersons = data;
        }
      });

    this.customerService.getLeadSourceTypes()
      .subscribe({
        next: (data) => {
          this.leadSourceTypes = data;
        },
        error: (error: string) => this.onError(error)
      });

    this.customerService.getStates()
      .subscribe({
        next: (data) => {
          this.states = data;
        },
        error: (error: string) => this.onError(error)
      });

    this.customerService.getPaymentTerms()
      .subscribe({
        next: (data) => {
          this.paymentTerms = data;
        },
        error: (error: string) => this.onError(error)
      });

    this.customerService.getPaymentTypes()
      .subscribe({
        next: (data) => {
          this.paymentTypes = data;
        },
        error: (error: string) => this.onError(error)
      });
  }

  loadOffices() {
    this.customerService.getOffices()
      .subscribe({
        next: (data) => {
          this.offices = data;
        },
        error: (error: string) => this.onError(error)
      });
  }

  customerUpdated(customer: Customer) {
    this.loadCustomer(customer);
  }

  loadCustomer(customer: Customer) {
    this.customer = customer as ProjectCustomerEdit;
    this.loadOffices();
    setTimeout(() => this.setNewCustomerOffice(), 3000)
  }

  setNewCustomerOffice() {
    if (this.customer?.customer_ID == null) {
      this.customer.office_ID = parseInt(this.offices.find(o => o.office_IsPrimary)?.office_ID);
    }
  }

  cancelEdit() {
    if (this.customer.customer_ID == null) {
      var proceed = confirm($localize`Are you sure you want to navigate away from this page? You will lose all changes made to this page if Submit is not selected.`);
      if (!proceed) return;

      this.router.navigate(['/customer/']);
      return;
    }

    var proceed = confirm($localize`Are you sure you want to undo changes?`);
    if (!proceed) return;

    this.isRequesting = true;
    this.customerService.getCustomer(this.customer.customer_ID)
      .pipe(finalize(() => this.isRequesting = false))
      .subscribe({
        next:(result) => {
          this.customer = result as ProjectCustomerEdit;
        },
        error: (error: string) => this.onError(error)
      }
      );
  }

  saveCustomer(form: NgForm) {
    this.errors = '';

    Object.keys(form.controls).forEach(key => {
      const controlErrors: ValidationErrors = form.controls[key].errors;
      if (controlErrors != null) {
        Object.keys(controlErrors).forEach(keyError => {
          console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
        });
      }
    });

    if (this.customer.customer_ID == null && !this.validateContactPhone(form))
      return;

    if (form.valid) {
      this.customerService.saveCustomer(this.customer)
        .pipe(finalize(() => this.isRequesting = false))
        .subscribe({
          next: (result) => {
            if (this.customer.customer_ID == null && result) {
              this.router.navigate(['/customer/' + result]);
            } else {
              this.updated.emit(this.customer);
              this.snackBar.open("Customer Info Updated", null, { duration: 2000, verticalPosition: 'bottom', panelClass: ['bidit-snackbar'] });
            }
          },
          error: (error: string) => this.onError(error)
        });
    }
  }

  validateContactPhone(form: NgForm): boolean {
    if ((this.customer.contact_Mobile == null || this.customer.contact_Mobile == '') &&
      (this.customer.contact_Phone == null || this.customer.contact_Phone == '') &&
      (this.customer.contact_Email == null || this.customer.contact_Email == '')) {

      form.controls['contact_Mobile'].setErrors({ 'incorrect': true });
      return false;
    }


    form.controls['contact_Mobile'].setErrors(null);
    return true;
  }
}
